import {difference, union} from 'lodash';
import React, {ReactNode, useCallback, useState} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {SettingsContentHeader} from '../../common/settingsContentHeader';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsWrapper} from '../../common/settingsWrapper';
import {ResourceTable} from './resourceTable';
import {ResourceColumn} from './resourceTableTypes';

/*
 * Props.
 */

interface ResourceListProps<T extends {id: unknown}> {
  tableColumns: ReadonlyArray<ResourceColumn<T>>;
  items: ReadonlyArray<T>;
  onLoadMore: Handler;
  moreStatus: AsyncMoreStatusesEnum;
  hasError: boolean;
  renderTableEmptyText: (hasError: boolean, hasSearchQuery: boolean) => ReactNode;

  createLabel?: ReactNode;
  onDeleteItems?: (ids: ReadonlyArray<T['id']>, items: ReadonlyArray<T>) => void;

  infoBox: ReactNode | null;

  searchQuery: string;
  resetSearchQuery: Handler;
  onSearchQueryChange: HandlerOf<string>;

  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: T['id']) => void;

  renderSelectedItemsLabel: (count: number) => ReactNode;
  renderDeleteItemsTitle: (count: number, item: T | undefined) => ReactNode;
  renderDeleteItemsDescription: (count: number) => ReactNode;
}

/*
 * Component.
 */

export const ResourceList = <T extends {id: unknown}>(props: ResourceListProps<T>) => {
  const {
    tableColumns,
    items,
    onLoadMore,
    moreStatus,
    hasError,
    renderTableEmptyText,
    createLabel,
    onDeleteItems,
    infoBox,
    searchQuery,
    resetSearchQuery,
    onSearchQueryChange,
    navigateTo,
    ...labels
  } = props;

  const [selectedIds, setSelectedIds] = useState<ReadonlyArray<T['id']>>([]);

  const toggleIds = useCallback(
    (shouldSelect: boolean, ids: ReadonlyArray<T['id']>) => {
      const newIds = shouldSelect ? union(selectedIds, ids) : difference(selectedIds, ids);

      setSelectedIds(newIds);
    },
    [selectedIds],
  );

  const onDelete = useCallback(() => {
    if (onDeleteItems) {
      onDeleteItems(
        selectedIds,
        items.filter((item) => selectedIds.includes(item.id)),
      );
      setSelectedIds([]);
    }
  }, [onDeleteItems, items, selectedIds]);

  const renderCreateButton = () => (
    <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => navigateTo(SettingsResourcePagesEnum.NEW)}>
      {createLabel}
    </Button>
  );

  return (
    <StyledSettingsWrapper>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <SettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={onSearchQueryChange}
            onExitClick={resetSearchQuery}
            renderButtons={createLabel ? renderCreateButton : undefined}
          />
          <ResourceTable
            isSidebarDisplayed={infoBox !== null}
            tableColumns={tableColumns}
            renderEmptyText={renderTableEmptyText}
            items={items}
            onLoadMore={onLoadMore}
            moreStatus={moreStatus}
            hasError={hasError}
            hasSearchQuery={searchQuery.length > 0}
            selectedIds={selectedIds}
            toggleIds={toggleIds}
            onDelete={onDelete}
            navigateTo={navigateTo}
            {...labels}
          />
        </StyledSettingsContent>
        {infoBox}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};
